<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="large" :isClosable="false" :isCloseButtonVisible="!isSaving">
    <template #content>
      <div
        :style="{
          opacity: isSaving ? '0.5' : '1',
          pointerEvents: isSaving ? 'none' : 'auto',
        }"
      >
        <div style="margin-bottom: 12px">
          <h1 class="global-h1" style="margin-bottom: 5px">
            {{ isCreateMode ? "Create a Social Post" : "Edit Post Guidance" }}
          </h1>
        </div>

        <div style="padding-bottom: 10px; border-bottom: 1px solid #e0e0e0;">
          <div style="display: flex;">
            <SrpSegmentedControl
              v-model="mode"
              :options="[
                { value: 'ai', label: 'AI Assisted', icon: 'ai_2' },
                { value: 'manual', label: 'Manual Mode', icon: 'edit-pencil_2' },
              ]"
              size="md"
              aria-label="Post creation mode"
            />
          </div>
          <p style="color: #666; font-size: 14px; line-height: 1.5; margin-top: 8px;">
            <span v-if="mode === 'ai'">Atlas will create a post based on your guidance</span>
            <span v-else>Or set the post details manually yourself</span>
          </p>
        </div>

        <div style="min-height: 300px;">
          <!-- AI Assisted Mode -->
          <div v-if="mode === 'ai'">
            <div style="margin-top: 20px">
              <label class="input-label">Theme/Purpose</label>
              <input
                :class="{
                  'global-text-input': true,
                  'global-text-input--error': !isFormValid && !_title,
                }"
                v-model="_title"
                style="max-width: 300px"
                placeholder="e.g. festival, local food..."
                type="text"
                @focus="isFormValid = true"
              />
            </div>

            <div style="margin-top: 16px">
              <label class="input-label">Guidance for the AI</label>
              <textarea
                :class="{
                  'global-textarea': true,
                  'global-textarea--error': !isFormValid && !_description,
                }"
                v-model="_description"
                style="height: 85px; min-height: 85px"
                placeholder="Describe the tone, style, key points to include..."
                @focus="isFormValid = true"
              ></textarea>
            </div>

            <input v-if="props.itemToEdit && _specificDate" class="global-text-input" v-model="_specificDate" style="max-width: 300px; margin-top: 12px" placeholder="Timing (optional)" type="text" />

            <div style="margin-top: 20px">
              <h4 style="margin-bottom: 8px">Content Album</h4>
              <div v-if="!selectedContentSummary">
                We'll find a content album to match with, or<br/>
                <LinkWithIcon  isDottedUnderline @click="isPickContentModalVisible = true" style="margin-top: 8px">
                  <template #icon>
                    <IconEmbedded name="folder_2" :size="19" />
                  </template>
                  <span>Manually choose one</span>
                </LinkWithIcon>
              </div>
              <div v-else style="margin-top: 8px">
                <ContentSummary
                  :contentSummaryData="selectedContentSummary"
                  :isUnbindButtonVisible="false"
                  :isContentSourcedFromLabelVisible="false"
                  :hideDecorations="true"
                  :customerId="props.customerId"
                  :isRemoveButtonVisible="true"
                  @clickViewPhotos="handleViewPhotos"
                  @clickRemove="selectedContentSummary = null"
                />
              </div>
            </div>
          </div>

          <!-- Manual Mode -->
          <div v-if="mode === 'manual'">
            <div style="margin-top: 20px">
              <label class="input-label">Caption</label>
              <textarea
                :class="{
                  'global-textarea': true,
                  'global-textarea--error': !isFormValid && !_caption,
                }"
                v-model="_caption"
                :maxlength="MAX_CAPTION_LENGTH"
                style="height: 85px; min-height: 85px"
                placeholder="Write your post caption..."
                @focus="isFormValid = true"
              ></textarea>
            </div>

            <div style="margin-top: 20px">
              <label class="input-label">Photos</label>
              
              <div
                :class="{
                  'photo-section-container': true,
                  'photo-section-container--error': !isFormValid && manualSelectedAssetIds.length === 0,
                }"
                @click="isFormValid = true"
              >
                <!-- Photo action buttons -->
                <div class="photo-actions">
                  <LinkWithIcon isDottedUnderline @click="isManualAlbumPickerOpen = true">
                    <template #icon><IconEmbedded name="folder_2" :size="19" /></template>
                    <span>{{ manualAlbumPayload ? "Change Album" : "Select from an Album" }}</span>
                  </LinkWithIcon>
                  <span class="photo-actions__separator"> </span>
                  <LinkWithIcon isDottedUnderline @click="isUploadSectionOpen = !isUploadSectionOpen">
                    <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
                    <span>Upload Photos</span>
                  </LinkWithIcon>
                </div>

                <!-- Upload section -->
                <PhotoUploadSection
                  v-if="isUploadSectionOpen"
                  inputId="new-post-manual-upload"
                  v-model="uploadedAssetIds"
                  :selectedAssetIds="manualSelectedAssetIds"
                  @select="handlePhotoUploadSelect"
                  @deselect="handlePhotoUploadDeselect"
                  @close="isUploadSectionOpen = false"
                />

                <!-- Album photos grid -->
                <div v-if="isLoadingManualAlbum" class="thumbnails-list thumbnails-list--skeleton">
                  <div v-for="i in 12" :key="i" class="thumbnail thumbnail--skeleton"></div>
                </div>

                <div v-else-if="manualAlbumPayload">
                  <ul class="thumbnails-list">
                    <SrpFileThumbnail
                      class="thumbnail"
                      v-for="assetId in displayedManualAlbumAssetIds"
                      :key="assetId"
                      :fileName="assetId"
                      galleryPostfix="manualAlbumSelection"
                      :isDeleteButton="false"
                      isEntireAreaCheckable
                    >
                      <input
                        type="checkbox"
                        :value="assetId"
                        :checked="manualSelectedAssetIds.includes(assetId)"
                        :disabled="isAtPhotoLimit && !manualSelectedAssetIds.includes(assetId)"
                        @change="$event => selectOrDeselectPhoto(($event.target as HTMLInputElement).checked, assetId)"
                      />
                    </SrpFileThumbnail>
                  </ul>
                </div>

                <!-- Sticky panel with warning and selected files -->
                <div class="sticky-panel">
                  <!-- Limit warning -->
                  <PhotoLimitWarning :isAtLimit="isAtPhotoLimit" />

                  <!-- Selected files panel -->
                  <SelectedFilesPanel
                    v-if="manualSelectedAssetIds.length"
                    class="selected-files-panel"
                    :selectedFiles="(manualSelectedAssetIds.map(id => ({ serverId: id })) as UploadedImage[])"
                    @remove="assetId => selectOrDeselectPhoto(false, assetId)"
                    @reorder="newArray => (manualSelectedAssetIds = newArray.map(({ serverId }) => serverId))"
                    :isPreviewLinkVisible="false"
                  />
                </div>
              </div>

              <div v-if="!isFormValid && manualSelectedAssetIds.length === 0" class="global-error-string" style="margin-top: 8px">
                Please select at least one photo
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex">
        <SrpButton
          fill="outlined"
          color="gray"
          @click="
            () => {
              emit('cancel');
              closeModal();
            }
          "
          :isDisabled="isSaving"
        >
          Cancel
        </SrpButton>

        <SrpButton
          @click="
            async () => {
              const success = await save();
              if (success) {
                closeModal();
              }
            }
          "
          :isDisabled="isSaving"
        >
          <template #icon>
            <LoadingIcon v-if="isSaving" :size="20" />
            <IconEmbedded v-else :name="isCreateMode ? 'ai_2' : 'save_2-5'" :size="20" />
          </template>
          {{ buttonText }}
        </SrpButton>
      </div>
    </template>
  </SrpModal>

  <PickFromExistingContentModal
    v-if="isPickContentModalVisible"
    v-model:isVisible="isPickContentModalVisible"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isPickContentModalVisible = false"
    @picked="contentSummary => handleContentSummarySelected(contentSummary)"
  />

  <ViewAllPhotosModal
    v-if="isViewAllPhotosModalVisible"
    v-model:isVisible="isViewAllPhotosModalVisible"
    :customerId="props.customerId"
    :contentSummaryId="viewAllPhotosSummaryId"
  />

  <PickFromExistingContentModal
    v-if="isManualAlbumPickerOpen"
    v-model:isVisible="isManualAlbumPickerOpen"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isManualAlbumPickerOpen = false"
    @picked="handleManualAlbumSelected"
  />
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { useToastsStore } from "@stores/toasts";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpSegmentedControl from "@components/ui/SrpSegmentedControl.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import PhotoUploadSection from "@components/SocialPostEditAndShareModal/PhotoUploadSection.vue";
import PhotoLimitWarning from "@components/SocialPostEditAndShareModal/PhotoLimitWarning.vue";

// Types
import { ContentCalendarItem, ContentCalendarItemMatch, ContentSummaryPreview } from "@contracts/contentCalendar";
import { UploadedImage } from "@contracts/uploadedImage";
import { SocialShareSummaryPayload } from "@contracts/shareStop";

// Repos
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Constants
import { MAX_PHOTOS_PER_POST, MAX_CAPTION_LENGTH } from "@/constants/social";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    itemToEdit?: ContentCalendarItem;
    customerId: string;
    weekGroup?: string | null;
    subTitle?: string;
  }>(),
  {
    itemToEdit: null,
    weekGroup: null,
    subTitle: "",
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "save", item: ContentCalendarItem): void;
  (e: "createCalendarItem", item: ContentCalendarItem): void;
  (e: "createNewItemAndPost", newItemAndPost: ContentCalendarItemMatch): void;
  (e: "cancel"): void;
}>();

// Local values ===============================================================
const _title = ref<string>("");
const _description = ref<string>("");
const _specificDate = ref<string>("");

// Mode toggle ================================================================
const mode = ref<string>("ai");

// Content group selection ====================================================
const selectedContentSummary = ref<ContentSummaryPreview | null>(null);
const isPickContentModalVisible = ref<boolean>(false);

function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  selectedContentSummary.value = contentSummary;
  isPickContentModalVisible.value = false;
}

// View all photos modal ======================================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");

function handleViewPhotos() {
  viewAllPhotosSummaryId.value = selectedContentSummary.value?.id || "";
  isViewAllPhotosModalVisible.value = true;
}

// Manual mode state ===========================================================
const _caption = ref<string>("");
const manualSelectedAssetIds = ref<string[]>([]);
const uploadedAssetIds = ref<string[]>([]);
const isUploadSectionOpen = ref<boolean>(false);
const isManualAlbumPickerOpen = ref<boolean>(false);
const manualAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const manualContentSummaryPreview = ref<ContentSummaryPreview | null>(null);
const manualUploadsSelectedCount = ref<number>(0);
const isLoadingManualAlbum = ref<boolean>(false);

onMounted(() => {
  _title.value = props.itemToEdit?.title ?? "";
  _description.value = props.itemToEdit?.description ?? "";
});

// Is Create Mode =============================================================
const isCreateMode = computed<boolean>(() => !props.itemToEdit);

// Button text =================================================================
const buttonText = computed<string>(() => {
  if (mode.value === "manual") {
    return isSaving.value ? "Creating" : "Create";
  }
  return isCreateMode.value ? (isSaving.value ? "Generating" : "Generate") : isSaving.value ? "Updating" : "Update";
});

// Validate form ==============================================================
const isFormValid = ref<boolean>(true);

// Manual mode helpers ========================================================
function selectOrDeselectPhoto(isSelected: boolean, assetId: string) {
  if (isSelected) {
    if (!manualSelectedAssetIds.value.includes(assetId)) {
      manualSelectedAssetIds.value.push(assetId);
    }
    // Clear validation error when a photo is selected
    if (manualSelectedAssetIds.value.length > 0) {
      isFormValid.value = true;
    }
  } else {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(id => id !== assetId);
  }
}

function handlePhotoUploadSelect(assetId: string) {
  manualUploadsSelectedCount.value++;
  selectOrDeselectPhoto(true, assetId);
}

function handlePhotoUploadDeselect(assetId: string) {
  manualUploadsSelectedCount.value--;
  selectOrDeselectPhoto(false, assetId);
}

async function handleManualAlbumSelected(contentSummary: ContentSummaryPreview) {
  manualContentSummaryPreview.value = contentSummary; // Store the preview for later
  isManualAlbumPickerOpen.value = false;
  isLoadingManualAlbum.value = true;
  manualAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(
    props.customerId,
    contentSummary.id
  );
  isLoadingManualAlbum.value = false;
}

const isAtPhotoLimit = computed(() => manualSelectedAssetIds.value.length >= MAX_PHOTOS_PER_POST);

const displayedManualAlbumAssetIds = computed(() => {
  if (manualAlbumPayload.value) {
    return manualAlbumPayload.value.assets;
  }
  return [];
});

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save(): Promise<boolean> {
  isSaving.value = true;

  if (mode.value === "manual") {
    // Manual mode validation and save
    if (!_caption.value?.trim() || manualSelectedAssetIds.value.length === 0) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    const response = await ContentCalendarRepo.createManualPost(props.customerId, {
      caption: _caption.value,
      assetIds: manualSelectedAssetIds.value,
      weekGroup: props.weekGroup,
      contentSourceType: manualContentSummaryPreview.value?.contextType,
      sourceEntityId: manualContentSummaryPreview.value?.contextId,
      manualUploadsSelected: manualUploadsSelectedCount.value,
    });

    // We have both the post and item created immediately
    emit("createNewItemAndPost", response.newItemAndPost);
    isSaving.value = false;
    return true;
  } else if (mode.value === "ai") {
    // AI mode validation and save
    if (!_title.value || !_description.value) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    const response = await ContentCalendarRepo.matchAndGeneratePost(props.customerId, {
      title: _title.value,
      description: _description.value,
      weekGroup: props.weekGroup,
      contentSourceType: selectedContentSummary.value?.contextType,
      sourceEntityId: selectedContentSummary.value?.contextId,
    });

    if (response.failedToMatch) {
      useToastsStore().warn({
        message: "Couldn't find suitable content",
        expiresInMs: 8000,
        details: "The guidance was created but we couldn't match it to any of your existing content.",
      });
    }

    // We just have the item created and the post will generate async
    emit("createCalendarItem", response.calendarItem);
    isSaving.value = false;
    return true;
  }
  else if (props.itemToEdit) {
    // Edit mode (shouldn't ever show so could retire this?)
    const updatedItem = await ContentCalendarRepo.updateItem(props.customerId, {
      ...props.itemToEdit,
      title: _title.value,
      description: _description.value,
    });

    emit("save", updatedItem);
    isSaving.value = false;
    return true;
  }
  else {
    getGlobalRemoteLogger().error(`NewPostModal: Unknown mode ${mode.value} for ${props.customerId}`);
    isSaving.value = false;
    return false;
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/loading-bg.scss";
@import "@/components/SocialPostEditAndShareModal/photo-upload";

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 3px !important;
}

// Photo section container ======================================================
.photo-section-container {
  padding: 12px;
  border-radius: 4px;
  transition: border-color 0.05s ease-in-out, box-shadow 0.05s ease-in-out;

  &--error {
    border: 1px rgba(178, 34, 34, 0.6) solid;
    box-shadow: 0 0 0 5px rgba(178, 34, 34, 0.2);
  }
}

// Photo actions =================================================================
.photo-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  &__separator {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
  }
}

// Upload section ================================================================
.upload-section {
  margin-top: 12px;
}

// Thumbnails list ==============================================================
.thumbnails-list {
  width: calc(100% + 15px);
  padding: 0;
  margin: 12px 0 20px;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &--skeleton {
    height: 200px;
    align-content: flex-start;
    position: relative;
    overflow: hidden;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.thumbnail {
  width: 113px;
  margin: 0 11px 11px 0;

  &--skeleton {
    @include loadingBgAnimation;
    height: 85px;
  }
}

// Sticky panel ===================================================================
.sticky-panel {
  background: white;
  position: sticky;
  bottom: -40px;
}

// Selected files panel =========================================================
.selected-files-panel {
  margin-top: 8px;
}
</style>
